import { Gift } from 'lucide-react'
import { formatPrice } from '../../lib/format'
import type { Giftable, GiftablesReadiness } from '../../lib/types'

import { Avatar, Button, Card, EmptyState, GameArt } from '../ui'

interface GiftableSectionProps {
  giftables: Giftable[]
  readiness: GiftablesReadiness
  onPropose: (giftable: Giftable) => void
  /** Open the existing proposal when giftable.purchase_id is set. */
  onViewProposal: (purchaseId: string) => void
  /** Wallet flag off ⇒ the list is informational only (no propose CTA). */
  walletFlagOn: boolean
  className?: string
}

// "Grace doesn't own this" / "Grace and Alan don't own this" (threshold is a
// server tunable — v1 always sends exactly one missing member).
function missingNames(giftable: Giftable): string {
  const names = giftable.missing_members.map((m) => m.display_name ?? 'One member')
  if (names.length <= 1) return names[0] ?? 'One member'
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * Giftable games for a group — every pick the server derived where all but
 * one member own the game (ADR-019 overlay; the client invents no ownership
 * logic). Any unknown-readiness member ⇒ the server sends [] and this section
 * explains why instead of guessing.
 */
export function GiftableSection({
  giftables,
  readiness,
  onPropose,
  onViewProposal,
  walletFlagOn,
  className,
}: GiftableSectionProps) {
  return (
    <Card className={className}>
      <div className="flex items-center gap-2">
        <Gift className="h-5 w-5 text-ember-300" strokeWidth={1.75} aria-hidden="true" />
        <h2 className="text-xl font-semibold text-text-strong">Gift a game</h2>
      </div>
      <p className="mt-1 text-sm text-text-muted">Games everyone but one of you owns.</p>

      {readiness.unknown_members > 0 ? (
        <p className="mt-4 text-sm text-warning-fg">
          We can&rsquo;t see everyone&rsquo;s library yet —{' '}
          {readiness.unknown_members === 1
            ? '1 member not synced'
            : `${readiness.unknown_members} members not synced`}
          . Gift picks unlock when every library is readable.
        </p>
      ) : giftables.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={<Gift className="h-8 w-8" strokeWidth={1.5} />}
          title="Nothing giftable right now"
          description="When everyone but one player owns a game, it shows up here."
        />
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {giftables.map((giftable) => {
            const missing = giftable.missing_members
            const purchaseId = giftable.purchase_id
            return (
              <li key={giftable.appid} className="flex flex-wrap items-center gap-4 py-4">
                <GameArt
                  appid={giftable.appid}
                  src={giftable.header_image}
                  name={giftable.name}
                  className="h-14 w-24 shrink-0"
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-semibold text-text-strong">
                      {giftable.name ?? `App ${giftable.appid}`}
                    </span>
                    {giftable.price ? (
                      <span className="text-sm text-text-muted">{formatPrice(giftable.price)}</span>
                    ) : (
                      <span className="text-sm text-text-faint">price unavailable</span>
                    )}
                  </div>
                  <p className="flex items-center gap-1.5 text-sm text-text-muted">
                    {missing[0] && (
                      <Avatar name={missing[0].display_name} src={missing[0].avatar_url} size="sm" className="h-5 w-5 text-[0.55rem]" />
                    )}
                    <span>
                      <span className="font-medium text-text-strong">{missingNames(giftable)}</span>{' '}
                      {missing.length === 1 ? 'doesn’t' : 'don’t'} own this — everyone else does
                    </span>
                  </p>
                </div>
                {walletFlagOn &&
                  (purchaseId ? (
                    <button
                      type="button"
                      onClick={() => onViewProposal(purchaseId)}
                      className="shrink-0 text-sm font-medium text-ember-300 transition hover:text-ember-400"
                    >
                      View proposal →
                    </button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!giftable.price}
                      onClick={() => onPropose(giftable)}
                    >
                      <Gift className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                      Propose gift
                    </Button>
                  ))}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}
